import { useState, useEffect } from 'react';

type Theme = 'light' | 'dark' | 'system';
type BackendTheme = 'light' | 'dark' | 'auto';

const THEME_STORAGE_KEY = 'theme-preference';

/**
 * Hook para manejar el tema de la aplicación (light/dark)
 * 
 * Características:
 * - Persiste la preferencia en localStorage
 * - Respeta la preferencia del sistema (prefers-color-scheme)
 * - Aplica/remueve la clase 'dark' en el elemento <html>
 * - Puede sincronizarse con el backend (mapea 'auto' a 'system')
 * 
 * Uso:
 * ```tsx
 * const { theme, setTheme, resolvedTheme, syncFromBackend } = useTheme();
 * 
 * // Cambiar tema
 * setTheme('dark');
 * setTheme('light');
 * setTheme('system'); // Usa preferencia del sistema
 * 
 * // Sincronizar desde backend
 * syncFromBackend('auto'); // 'auto' se mapea a 'system'
 * 
 * // Ver tema actual resuelto (útil para iconos)
 * const isDark = resolvedTheme === 'dark';
 * ```
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Intentar leer de localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
      if (stored && ['light', 'dark', 'system'].includes(stored)) {
        return stored;
      }
    }
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  // Resolver el tema basado en preferencia del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const resolveTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };

    resolveTheme();

    // Escuchar cambios en la preferencia del sistema
    const handler = () => resolveTheme();
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme]);

  // Aplicar clase 'dark' al elemento <html>
  useEffect(() => {
    const root = document.documentElement;
    
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Función para mapear tema del backend al frontend
  const mapBackendTheme = (backendTheme: BackendTheme | null | undefined): Theme => {
    if (!backendTheme) return 'system';
    if (backendTheme === 'auto') return 'system';
    return backendTheme;
  };

  // Función para mapear tema del frontend al backend
  const mapToBackendTheme = (frontendTheme: Theme): BackendTheme => {
    if (frontendTheme === 'system') return 'auto';
    return frontendTheme;
  };

  // Función para cambiar el tema
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  // Función para sincronizar desde el backend
  const syncFromBackend = (backendTheme: BackendTheme | null | undefined) => {
    const mappedTheme = mapBackendTheme(backendTheme);
    setTheme(mappedTheme);
  };

  // Toggle rápido entre light y dark
  const toggleTheme = () => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  return {
    theme,           // Preferencia guardada: 'light' | 'dark' | 'system'
    setTheme,        // Función para cambiar la preferencia
    resolvedTheme,   // Tema actual resuelto: 'light' | 'dark'
    toggleTheme,     // Toggle rápido
    isDark: resolvedTheme === 'dark',
    syncFromBackend, // Sincronizar desde backend (mapea 'auto' a 'system')
    mapToBackendTheme, // Mapear a formato backend (mapea 'system' a 'auto')
  };
}


